/**
 * Course Learn Page Component
 * The actual course learning interface with navigator and content
 */

import { presentMutationError, toastError } from "../../../utils/showApiErrorToast";
import {
  Flex,
  Box,
  Button as ChakraButton,
  useColorModeValue,
  Icon,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
} from "@chakra-ui/react";
import { toast } from "sonner";
import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useParams, useHistory, useLocation } from "react-router-dom";
import { ChevronLeft, ChevronRight, Menu } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { FieldLabel } from "@/components/ui/field";
import { useShadcnInsetChrome } from "@/contexts/ShadcnInsetChromeContext";
import { useShadcnInsetBreadcrumbTrail } from "@/hooks/useShadcnInsetBreadcrumbTrail";
import { cn } from "@/lib/utils";
import type { BreadcrumbCrumb } from "@/utils/breadcrumbPageTitle";
import { universityApi } from "../../../services/universityApi";
import type { Course as BackendCourse } from "../../../types/university";
import PortableTextRenderer from "../../../components/University/PortableTextRenderer";
import { StatisticsEmptyState, statisticsPageMinHeight } from "../Statistics/components/statisticsEmptyState";
import {
  courseLearnMainCardClassName,
  universityCourseBodyTextClassName,
  universityCourseFieldLabelClassName,
  universityCourseMutedTextClassName,
} from "./courseOverviewCardShell";
import type { CourseOverviewDetails } from "./CourseOverviewView";
import { UniversityCourseLessonNavigator } from "./UniversityCourseLessonNavigator";
import { UniversityCourseLoadingState } from "./UniversityCourseLoadingState";
import { buildUniversityLessonCardModel } from "./universityLessonCardUtils";
import {
  buildUniversityLessonLinkIndex,
  normalizePortableTextMarkdownLinks,
  parseUniversityLessonPath,
  resolveUniversityLessonHref,
} from "./universityLessonLinks";
import { resolveHelpCentreHref } from "./universityHelpCentreLinks";
import { CourseOverviewProgress } from "./CourseOverviewProgress";
import {
  computeCourseCompletionPercentage,
  countCourseLessons,
} from "./universityCourseProgress";

type CourseLearnLocationState = {
  courseTitle?: string;
  lessonTitle?: string;
};

const CourseLearn: React.FC = () => {
  const { courseId, moduleId, lessonId } = useParams<{
    courseId: string;
    moduleId: string;
    lessonId: string;
  }>();
  const history = useHistory();
  const location = useLocation<CourseLearnLocationState>();
  const shadcnInset = useShadcnInsetChrome();
  const pageMinHeight = statisticsPageMinHeight(shadcnInset);
  const FlexAny = Flex as any;
  const { isOpen: isNavigatorOpen, onOpen: onNavigatorOpen, onClose: onNavigatorClose } = useDisclosure();
  const courseTitleFromState = location.state?.courseTitle;
  const lessonTitleFromState = location.state?.lessonTitle;

  const textColor = useColorModeValue("#575757", "white");
  const cardBg = useColorModeValue("white", "#151517");
  const tropicalSurfGradient = "linear-gradient(135deg, #38BBD1, #3EEAEC)";

  const pageShellProps = {
    "data-university-page": "",
    "data-university-course-learn-page": "",
    direction: "column" as const,
    pt: shadcnInset ? 0 : { base: "100px", md: "80px", lg: "40px" },
    pb: "24px",
    px: shadcnInset ? 0 : { base: "20px", xl: "40px" },
    gap: "24px",
    w: "100%",
    minW: 0,
    minH: pageMinHeight,
    flex: 1,
  };

  // Backend course + loading state
  const [backendCourse, setBackendCourse] = useState<BackendCourse | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [completingLesson, setCompletingLesson] = useState<boolean>(false);

  // Completion data - synced with backend progress
  const [completedLessons, setCompletedLessons] = useState<Set<string>>(new Set());

  // Helper to get module and lesson slugs from numeric IDs
  const getSlugsFromIds = (moduleId: number, lessonId: number): { moduleSlug: string; lessonSlug: string } | null => {
    if (!backendCourse) return null;
    
    const modules = backendCourse.modules || [];
    const moduleIndex = moduleId - 1; // Convert 1-based to 0-based index
    
    if (moduleIndex < 0 || moduleIndex >= modules.length) return null;
    
    const module = modules[moduleIndex];
    const lessons = module.lessons || [];
    const lessonIndex = lessonId - 1; // Convert 1-based to 0-based index
    
    if (lessonIndex < 0 || lessonIndex >= lessons.length) return null;
    
    const lesson = lessons[lessonIndex];
    
    // Extract slug - handle both { current: string } and string formats
    const moduleSlug = typeof module.slug === 'object' && module.slug !== null 
      ? module.slug.current 
      : typeof module.slug === 'string' 
        ? module.slug 
        : "";
    
    const lessonSlug = typeof lesson.slug === 'object' && lesson.slug !== null 
      ? lesson.slug.current 
      : typeof lesson.slug === 'string' 
        ? lesson.slug 
        : "";
    
    if (!moduleSlug || !lessonSlug) return null;
    
    return { moduleSlug, lessonSlug };
  };

  // Helper to check if lesson is completed
  const isLessonCompleted = useCallback((moduleId: number, lessonId: number) => {
    return completedLessons.has(`${moduleId}-${lessonId}`);
  }, [completedLessons]);


  // Helper to sync completed lessons from backend
  const syncCompletedLessonsFromBackend = React.useCallback(async (course: BackendCourse) => {
    if (!course.enrollment || !course.modules || !courseId) return;
    
    // Fetch completion status for all lessons
    const completedSet = new Set<string>();
    
    // Fetch lesson progress for all lessons in parallel (but limit concurrency)
    const progressPromises: Promise<void>[] = [];
    
    course.modules.forEach((module, moduleIndex) => {
      const moduleId = moduleIndex + 1; // 1-based ID
      const moduleSlug = typeof module.slug === 'object' && module.slug !== null 
        ? module.slug.current 
        : typeof module.slug === 'string' 
          ? module.slug 
          : "";
      
      if (!moduleSlug || !module.lessons) return;
      
      module.lessons.forEach((lesson, lessonIndex) => {
        const lessonId = lessonIndex + 1; // 1-based ID
        const lessonSlug = typeof lesson.slug === 'object' && lesson.slug !== null 
          ? lesson.slug.current 
          : typeof lesson.slug === 'string' 
            ? lesson.slug 
            : "";
        
        if (!lessonSlug) return;
        
        // Fetch lesson progress
        const promise = universityApi.getLessonProgress(courseId, moduleSlug, lessonSlug)
          .then((progress) => {
            if (progress?.is_completed) {
              completedSet.add(`${moduleId}-${lessonId}`);
            }
          })
          .catch((error) => {
            // Silently fail for individual lessons - they might not have progress yet
            console.debug(`[CourseLearn] Could not fetch progress for lesson ${moduleSlug}/${lessonSlug}:`, error);
          });
        
        progressPromises.push(promise);
      });
    });
    
    // Wait for all progress fetches to complete
    await Promise.all(progressPromises);
    
    // Update state with completed lessons
    setCompletedLessons(completedSet);
  }, [courseId]);

  // Load course structure from backend
  useEffect(() => {
    let isMounted = true;

    const fetchCourse = async () => {
      setLoading(true);
      setError(null);
      setBackendCourse(null);
      try {
        // Primary: fetch single course by ID
        const data = await universityApi.getCourse(courseId);
        if (!isMounted) return;
        setBackendCourse(data);
        
        // Sync completed lessons from backend if user is enrolled
        if (data.enrollment) {
          await syncCompletedLessonsFromBackend(data);
        }
      } catch (e) {
        if (!isMounted) return;
        const message = e instanceof Error ? e.message : "Failed to load course.";

        // Fallback: try to find the course from the full list
        try {
          const list = await universityApi.getCourses();
          if (!isMounted) return;
          const found = list.find((c) => c._id === courseId);
          if (found) {
            setBackendCourse(found);
            setError(null);
            // Sync completed lessons if enrolled
            if (found.enrollment) {
              await syncCompletedLessonsFromBackend(found);
            }
            return;
          }
        } catch {
          // ignore fallback errors
        }

        setError(message);
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchCourse();

    return () => {
      isMounted = false;
    };
  }, [courseId, syncCompletedLessonsFromBackend]);

  // Unenrolled users cannot access lesson content — send them back to overview.
  useEffect(() => {
    if (loading || !backendCourse || !courseId) return;
    if (backendCourse.enrollment) return;

    history.replace(`/university/course/${courseId}`, {
      courseTitle: backendCourse.title,
      enrollRequired: true,
    });
  }, [backendCourse, courseId, history, loading]);

  // Map backend course into local UI-friendly structure for navigator / IDs
  const courseDetails = React.useMemo((): CourseOverviewDetails | null => {
    if (!backendCourse) return null;

    const modules = backendCourse.modules || [];
    const lessonImageGradient = "linear-gradient(135deg, #38BBD1 0%, #3EEAEC 100%)";

    return {
      longDescription: backendCourse.description || backendCourse.shortDescription || "",
      learningObjectives: [],
      modules: modules.map((module, moduleIndex) => {
        const lessons = module.lessons || [];
        return {
          id: moduleIndex + 1,
          title: module.title,
          description: module.description || "",
          duration: "",
          lessons: lessons.map((lesson, lessonIndex) => {
            const card = buildUniversityLessonCardModel(lesson, {
              excerptMaxLength: 64,
            });
            return {
              id: lessonIndex + 1,
              title: card.title,
              description: card.description,
              imageGradient: lessonImageGradient,
              readingTimeLabel: card.readingTimeLabel,
            };
          }),
        };
      }),
    };
  }, [backendCourse]);

  const selectedLesson = useMemo(() => {
    if (!courseDetails || !moduleId || !lessonId) return null;

    const moduleIdNum = parseInt(moduleId, 10);
    const lessonIdNum = parseInt(lessonId, 10);
    const thisModule = courseDetails.modules.find((module) => module.id === moduleIdNum);
    if (thisModule?.lessons.some((lesson) => lesson.id === lessonIdNum)) {
      return { moduleId: moduleIdNum, lessonId: lessonIdNum };
    }

    return null;
  }, [courseDetails, moduleId, lessonId]);

  const isModuleCompleted = useCallback(
    (moduleId: number) => {
      if (!courseDetails) return false;
      const module = courseDetails.modules.find((item) => item.id === moduleId);
      if (!module) return false;
      return module.lessons.every((lesson) => isLessonCompleted(module.id, lesson.id));
    },
    [courseDetails, isLessonCompleted],
  );

  const isModuleInProgress = useCallback(
    (moduleId: number) => {
      if (!courseDetails) return false;
      if (isModuleCompleted(moduleId)) return false;
      const module = courseDetails.modules.find((item) => item.id === moduleId);
      if (!module) return false;
      return module.lessons.some((lesson) => isLessonCompleted(module.id, lesson.id));
    },
    [courseDetails, isLessonCompleted, isModuleCompleted],
  );

  const lessonBreadcrumbTrail = useMemo((): BreadcrumbCrumb[] => {
    const courseLabel =
      backendCourse?.title?.trim() ||
      courseTitleFromState?.trim() ||
      "Course";

    let lessonLabel = lessonTitleFromState?.trim();
    if (!lessonLabel && courseDetails && moduleId && lessonId) {
      const moduleIdNum = parseInt(moduleId, 10);
      const lessonIdNum = parseInt(lessonId, 10);
      lessonLabel = courseDetails.modules
        .find((module) => module.id === moduleIdNum)
        ?.lessons.find((item) => item.id === lessonIdNum)
        ?.title?.trim();
    }
    if (!lessonLabel) {
      lessonLabel = "Lesson";
    }

    if (!courseId) {
      return [
        { label: "University", to: "/university" },
        { label: lessonLabel },
      ];
    }

    return [
      { label: "University", to: "/university" },
      { label: courseLabel, to: `/university/course/${courseId}` },
      { label: lessonLabel },
    ];
  }, [
    backendCourse?.title,
    courseDetails,
    courseId,
    courseTitleFromState,
    lessonTitleFromState,
    moduleId,
    lessonId,
  ]);

  useShadcnInsetBreadcrumbTrail(lessonBreadcrumbTrail);

  // Record lesson access so Continue course can resume the current lesson.
  useEffect(() => {
    if (!courseId || !selectedLesson || !backendCourse?.enrollment) return;

    const slugs = getSlugsFromIds(selectedLesson.moduleId, selectedLesson.lessonId);
    if (!slugs?.moduleSlug || !slugs.lessonSlug) return;

    void universityApi
      .getLesson(courseId, slugs.moduleSlug, slugs.lessonSlug)
      .catch(() => undefined);
  }, [courseId, selectedLesson, backendCourse?.enrollment]);

  // Redirect to first lesson when course loads without a lesson in the URL
  useEffect(() => {
    if (courseDetails && !lessonId) {
      const firstModule = courseDetails.modules[0];
      if (firstModule && firstModule.lessons.length > 0) {
        history.replace(
          `/university/course/${courseId}/module/${firstModule.id}/lesson/${firstModule.lessons[0].id}`,
        );
      }
    }
  }, [courseDetails, lessonId, courseId, history]);

  const handleSelectLesson = useCallback(
    (targetModuleId: number, targetLessonId: number) => {
      if (!courseDetails || !backendCourse) return;

      const module = courseDetails.modules.find((item) => item.id === targetModuleId);
      const lesson = module?.lessons.find((item) => item.id === targetLessonId);

      history.push(
        `/university/course/${courseId}/module/${targetModuleId}/lesson/${targetLessonId}`,
        {
          courseTitle: backendCourse.title,
          ...(lesson?.title ? { lessonTitle: lesson.title } : {}),
        },
      );
    },
    [backendCourse, courseDetails, courseId, history],
  );

  const lessonLinkIndex = useMemo(
    () => buildUniversityLessonLinkIndex(backendCourse?.modules),
    [backendCourse?.modules],
  );

  const resolveInternalHref = useCallback(
    (href: string) => {
      if (!courseId) return null;
      return resolveUniversityLessonHref(href, lessonLinkIndex, courseId);
    },
    [courseId, lessonLinkIndex],
  );

  const handleInternalNavigate = useCallback(
    (path: string) => {
      const parsed = parseUniversityLessonPath(path);
      if (parsed && courseDetails && backendCourse) {
        handleSelectLesson(parsed.moduleId, parsed.lessonId);
        return;
      }
      history.push(path);
    },
    [backendCourse, courseDetails, handleSelectLesson, history],
  );

  const selectedModule =
    courseDetails?.modules.find((module) => module.id === selectedLesson?.moduleId) ?? null;

  const currentLessonContent = useMemo(() => {
    if (!backendCourse || !courseDetails || !selectedLesson) return null;

    const moduleIdx = courseDetails.modules.findIndex(
      (module) => module.id === selectedLesson.moduleId,
    );
    const backendModules = backendCourse.modules || [];
    if (moduleIdx < 0 || !backendModules[moduleIdx]) return null;

    const lessonIdx = courseDetails.modules[moduleIdx].lessons.findIndex(
      (lesson) => lesson.id === selectedLesson.lessonId,
    );
    const backendLessons = backendModules[moduleIdx].lessons || [];
    if (lessonIdx < 0 || !backendLessons[lessonIdx]) return null;

    // @ts-ignore - backend lesson shape is compatible
    const raw = backendLessons[lessonIdx].content ?? null;
    return normalizePortableTextMarkdownLinks(raw) ?? null;
  }, [backendCourse, courseDetails, selectedLesson]);

  if (loading) {
    return (
      <UniversityCourseLoadingState
        courseTitle={courseTitleFromState ?? backendCourse?.title}
        label={lessonTitleFromState}
        description="Please wait while we load your course."
      />
    );
  }

  if (error || !backendCourse) {
    return (
      <FlexAny {...pageShellProps}>
        <StatisticsEmptyState
          variant="no-data"
          fillParent
          title="Course not found"
          description={error ?? "We couldn't load this course."}
        />
        <Flex justify="center">
          <ChakraButton onClick={() => history.push("/university")} bgGradient={tropicalSurfGradient} color="white">
            Back to Courses
          </ChakraButton>
        </Flex>
      </FlexAny>
    );
  }

  const currentModuleTitle = selectedModule?.title || courseDetails?.modules[0]?.title || "";
  const currentModuleNumber = selectedModule?.id || courseDetails?.modules[0]?.id || 1;
  const currentLessonTitle =
    selectedModule?.lessons.find((lesson) => lesson.id === selectedLesson?.lessonId)?.title ?? "";

  // Find previous and next lessons (all lessons are accessible)
  const getPreviousLesson = () => {
    if (!selectedLesson || !courseDetails) return null;
    const currentModuleIndex = courseDetails.modules.findIndex(m => m.id === selectedLesson.moduleId);
    const currentLessonIndex = selectedModule?.lessons.findIndex(l => l.id === selectedLesson.lessonId) ?? -1;
    
    if (currentLessonIndex > 0) {
      // Previous lesson in same module
      const prevLesson = selectedModule?.lessons[currentLessonIndex - 1];
      if (prevLesson) {
        return { moduleId: selectedLesson.moduleId, lessonId: prevLesson.id };
      }
    } else if (currentModuleIndex > 0) {
      // Last lesson of previous module
      const prevModule = courseDetails.modules[currentModuleIndex - 1];
      const lastLesson = prevModule.lessons[prevModule.lessons.length - 1];
      if (lastLesson) {
        return { moduleId: prevModule.id, lessonId: lastLesson.id };
      }
    }
    return null;
  };

  const getNextLesson = () => {
    if (!selectedLesson || !courseDetails) return null;
    const currentModuleIndex = courseDetails.modules.findIndex(m => m.id === selectedLesson.moduleId);
    const currentLessonIndex = selectedModule?.lessons.findIndex(l => l.id === selectedLesson.lessonId) ?? -1;
    
    if (currentLessonIndex < (selectedModule?.lessons.length ?? 0) - 1) {
      // Next lesson in same module
      const nextLesson = selectedModule?.lessons[currentLessonIndex + 1];
      if (nextLesson) {
        return { moduleId: selectedLesson.moduleId, lessonId: nextLesson.id };
      }
    } else if (currentModuleIndex < courseDetails.modules.length - 1) {
      // First lesson of next module
      const nextModule = courseDetails.modules[currentModuleIndex + 1];
      if (nextModule.lessons.length > 0) {
        return { moduleId: nextModule.id, lessonId: nextModule.lessons[0].id };
      }
    }
    return null;
  };

  const previousLesson = getPreviousLesson();
  const nextLesson = getNextLesson();

  const totalLessons = countCourseLessons(courseDetails);
  const courseProgressPercent = computeCourseCompletionPercentage(
    completedLessons.size,
    totalLessons,
  );

  // Check if current lesson is completed
  const isCurrentLessonCompleted = selectedLesson 
    ? isLessonCompleted(selectedLesson.moduleId, selectedLesson.lessonId)
    : false;

  // Handler to mark current lesson as complete
  const handleMarkAsComplete = async (isChecked: boolean) => {
    if (!selectedLesson || !courseId) return;
    
    const key = `${selectedLesson.moduleId}-${selectedLesson.lessonId}`;
    
    // Get slugs for API call
    const slugs = getSlugsFromIds(selectedLesson.moduleId, selectedLesson.lessonId);
    if (!slugs || !slugs.moduleSlug || !slugs.lessonSlug) {
      toastError("Error", { description: "Could not find lesson information" });
      return;
    }
    
    // Optimistic update: update UI immediately
    const previousState = completedLessons.has(key);
    setCompletedLessons(prev => {
      const newSet = new Set(prev);
      if (isChecked) {
        newSet.add(key);
      } else {
        newSet.delete(key);
      }
      return newSet;
    });
    
    setCompletingLesson(true);
    try {
      let response;
      
      if (isChecked) {
        // Call API to mark lesson as complete
        response = await universityApi.markLessonComplete(
          courseId,
          slugs.moduleSlug,
          slugs.lessonSlug,
          0 // timeSpentSeconds - can be enhanced later with time tracking
        );
      } else {
        // Call API to mark lesson as incomplete
        response = await universityApi.markLessonIncomplete(
          courseId,
          slugs.moduleSlug,
          slugs.lessonSlug
        );
      }
      
      // ✅ Update state from mutation response - this is the source of truth
      // The response contains the authoritative is_completed status
      if (response.is_completed) {
        setCompletedLessons(prev => {
          const newSet = new Set(prev);
          newSet.add(key);
          return newSet;
        });
      } else {
        setCompletedLessons(prev => {
          const newSet = new Set(prev);
          newSet.delete(key);
          return newSet;
        });
      }
      
      // Show success notification
      isChecked
        ? toast.success("Lesson completed!", { description: "Great progress! Keep it up." })
        : toast.info("Lesson marked as incomplete", { description: "You can complete it again when ready." });

      // Refresh course progress summary so overview / catalog stay in sync.
      // Local completedLessons set already drives the in-page progress bar.
      try {
        const refreshed = await universityApi.getCourse(courseId);
        setBackendCourse(refreshed);
      } catch {
        // Non-fatal: local completion UI is already updated
      }
    } catch (error) {
      // Revert optimistic update on error (only for complete action, incomplete handled above)
      setCompletedLessons(prev => {
        const newSet = new Set(prev);
        if (previousState) {
          newSet.add(key);
        } else {
          newSet.delete(key);
        }
        return newSet;
      });
      
      presentMutationError(error, "Error");
    } finally {
      setCompletingLesson(false);
    }
  };

  return (
    <FlexAny {...pageShellProps}>
      <ChakraButton
        display={{ base: "flex", lg: "none" }}
        alignSelf="flex-start"
        leftIcon={<Icon as={Menu} />}
        onClick={onNavigatorOpen}
        bgGradient={tropicalSurfGradient}
        color="white"
        fontWeight="medium"
        borderRadius="8px"
        px="20px"
        py="10px"
        h="auto"
        fontFamily="'IBM Plex Sans', sans-serif"
        textTransform="uppercase"
        fontSize="sm"
      >
        Open Course Navigator
      </ChakraButton>

      <div
        data-university-course-learn-main-card=""
        className={cn(courseLearnMainCardClassName, "flex min-h-0 flex-1 flex-col overflow-hidden")}
      >
        <Flex direction={{ base: "column", lg: "row" }} gap="0" align="stretch" flex="1" minH="0" minW="0" overflow="hidden" w="100%">
            {/* Left Column - Course Content Sidebar — fixed width so accordion open does not steal from content */}
            <Box
              flex={{ base: "1", lg: "0 0 auto" }}
              flexShrink={0}
              w={{ base: "100%", lg: "min(400px, 30%)" }}
              maxW={{ lg: "400px" }}
              minW={{ lg: 0 }}
              minH="0"
              overflow="hidden"
              display={{ base: "none", lg: "flex" }}
              flexDirection="column"
              borderRightWidth={{ lg: "1px" }}
              borderRightStyle={{ lg: "solid" }}
              borderRightColor={{ lg: "var(--border)" }}
            >
              <Box
                p="24px"
                flex="1"
                minH="0"
                minW="0"
                display="flex"
                flexDirection="column"
                overflowY="auto"
                overflowX="hidden"
                sx={{ scrollbarGutter: "stable" }}
              >
                {courseDetails ? (
                  <UniversityCourseLessonNavigator
                    modules={courseDetails.modules}
                    selectedLesson={selectedLesson}
                    isLessonCompleted={isLessonCompleted}
                    isModuleCompleted={isModuleCompleted}
                    isModuleInProgress={isModuleInProgress}
                    onSelectLesson={handleSelectLesson}
                    compact
                  />
                ) : null}
              </Box>
            </Box>

            {/* Right Column - Main Content Area — flex grow fills remainder after fixed sidebar */}
            <Box
              flex={{ base: "1", lg: "1 1 0" }}
              minW="0"
              minH="0"
              overflowY="auto"
              p="24px"
            >
              <div className="flex flex-col gap-6" data-university-course-learn-content="">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                  <div className="flex min-w-0 flex-col gap-1.5">
                    <p
                      data-university-course-muted=""
                      className={cn("text-sm", universityCourseMutedTextClassName)}
                    >
                      {`Module ${currentModuleNumber} - ${currentModuleTitle}`}
                    </p>
                    <h1
                      data-university-course-lesson-title=""
                      className={cn(
                        "font-[family-name:var(--font-primary)] text-2xl font-semibold leading-tight",
                        universityCourseFieldLabelClassName,
                      )}
                    >
                      {currentLessonTitle || "Lesson"}
                    </h1>
                  </div>
                  {backendCourse?.enrollment && totalLessons > 0 ? (
                    <CourseOverviewProgress value={courseProgressPercent} />
                  ) : null}
                </div>

                {currentLessonContent ? (
                  <PortableTextRenderer
                    content={currentLessonContent}
                    resolveInternalHref={resolveInternalHref}
                    resolveExternalHref={resolveHelpCentreHref}
                    onInternalNavigate={handleInternalNavigate}
                  />
                ) : (
                  <p
                    data-university-course-overview-body=""
                    className={universityCourseBodyTextClassName}
                  >
                    Lesson content will appear here once it has been added in Sanity.
                  </p>
                )}

                <div className="flex flex-col gap-4 border-t border-border pt-6 sm:flex-row sm:items-center sm:justify-between">
                  <Button
                    type="button"
                    variant="outline-gray"
                    size="sm"
                    data-font="primary"
                    disabled={!previousLesson}
                    onClick={() => {
                      if (previousLesson) {
                        handleSelectLesson(previousLesson.moduleId, previousLesson.lessonId);
                      }
                    }}
                  >
                    <ChevronLeft className="size-4" aria-hidden />
                    <span className="normal-case tracking-normal">Previous lesson</span>
                  </Button>

                  <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                    <div className="flex items-center gap-2">
                      <Checkbox
                        id="university-lesson-complete"
                        data-university-course-lesson-complete=""
                        checked={isCurrentLessonCompleted}
                        disabled={completingLesson || !selectedLesson}
                        onCheckedChange={(checked) => {
                          void handleMarkAsComplete(checked === true);
                        }}
                      />
                      <FieldLabel
                        htmlFor="university-lesson-complete"
                        className={cn(
                          universityCourseMutedTextClassName,
                          "cursor-pointer text-sm font-normal",
                        )}
                      >
                        {isCurrentLessonCompleted ? "Completed" : "Mark as complete"}
                      </FieldLabel>
                    </div>

                    <Button
                      type="button"
                      variant="gradient"
                      size="sm"
                      data-font="primary"
                      className="shrink-0"
                      disabled={!nextLesson}
                      onClick={() => {
                        if (nextLesson) {
                          handleSelectLesson(nextLesson.moduleId, nextLesson.lessonId);
                        }
                      }}
                    >
                      <span className="normal-case tracking-normal">Next lesson</span>
                      <ChevronRight className="size-4" aria-hidden />
                    </Button>
                  </div>
                </div>
              </div>
            </Box>
        </Flex>
      </div>

      {/* Mobile Course Navigator Modal */}
      <Modal isOpen={isNavigatorOpen} onClose={onNavigatorClose} size="full" isCentered>
        <ModalOverlay backdropFilter="blur(10px)" bg="rgba(0, 0, 0, 0.1)" />
        <ModalContent bg={cardBg} borderRadius="md" maxH="90vh" m="20px">
          <ModalHeader 
            color={textColor} 
            fontFamily="'Urbanist', sans-serif" 
            fontWeight="bold"
            fontSize="xl"
          >
            Course Content
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody pb="24px" overflowY="auto">
            {courseDetails ? (
              <UniversityCourseLessonNavigator
                modules={courseDetails.modules}
                selectedLesson={selectedLesson}
                isLessonCompleted={isLessonCompleted}
                isModuleCompleted={isModuleCompleted}
                isModuleInProgress={isModuleInProgress}
                compact
                onSelectLesson={(targetModuleId, targetLessonId) => {
                  handleSelectLesson(targetModuleId, targetLessonId);
                  onNavigatorClose();
                }}
              />
            ) : null}
          </ModalBody>
        </ModalContent>
      </Modal>
    </FlexAny>
  );
};

export default CourseLearn;
